<template>
  <Teleport to="body">
    <!-- Unsubscribe Confirmation Modal -->
    <div
      v-show="showUnsubscribeModal"
      class="modal-overlay"
      :class="{ show: unsubscribeModalVisible }"
      @click.self="hideUnsubscribeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">{{ t("web.button.unsubscribe") }}</h3>
        </div>
        <div class="modal-body">
          <p>
            {{ t("web.subscription.message.unsubscribe_confirm") }}
          </p>
          <template v-if="newsletter.unsubscription_reasons?.length">
            <p>
              {{ t("web.subscription.message.unsubscribe_reason_prompt") }}
            </p>
            <div class="unsubscription-reasons-list">
              <label
                v-for="reason in newsletter.unsubscription_reasons"
                :key="reason.id"
              >
                <input
                  v-model="selectedReason"
                  type="radio"
                  name="unsubscription-reason"
                  :value="reason.id"
                />
                <div>{{ reason.label }}</div>
              </label>
              <label>
                <input
                  v-model="selectedReason"
                  type="radio"
                  name="unsubscription-reason"
                  value="none-of-the-above"
                />
                {{ t("web.form.option.none_of_above") }}
              </label>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button
            class="portal-btn portal-btn--ghost"
            type="button"
            @click="hideUnsubscribeModal"
          >
            {{ t("web.button.cancel") }}
          </button>
          <button
            class="portal-btn portal-btn--danger-solid"
            type="button"
            @click="confirmUnsubscribe"
          >
            {{ t("web.button.unsubscribe") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal for Unactivated Subscribers -->
    <div
      v-show="showConfirmationModal"
      class="modal-overlay"
      :class="{ show: confirmationModalVisible }"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ t("web.subscription.message.almost_there") }}
          </h3>
        </div>
        <div class="modal-body">
          <p>
            {{ t("web.subscription.message.confirmation_email_sent") }}
            <strong>{{ subscriber?.email_address }}</strong
            >.
            {{ t("web.subscription.message.click_to_confirm") }}
          </p>
        </div>
        <div class="modal-footer">
          <button
            class="portal-btn portal-btn--primary"
            type="submit"
            @click="handleRefresh"
          >
            {{ t("web.subscription.action.email_confirmed") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Upgrade-to-premium Modal -->
    <div
      v-if="showUpgradeModal"
      class="modal-overlay"
      :class="{ show: upgradeModalVisible }"
      @click.self="hideUpgradeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ t("web.subscription.label.upgrade_subscription") }}
          </h3>
        </div>
        <div class="modal-body">
          <PaymentElement
            :newsletter="newsletter"
            :email-address="subscriber?.email_address"
            already-subscribed
          />
        </div>
      </div>
    </div>
    <!-- Change-plan Modal -->
    <div
      v-if="showChangePlanModal"
      class="modal-overlay"
      :class="{ show: changePlanModalVisible }"
      @click.self="hideChangePlanModal"
    >
      <div class="modal-content modal-content--wide">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ t("web.subscription.action.change_plan") }}
          </h3>
          <p class="modal-subtitle">
            {{ t("web.subscription.message.change_plan_subtitle") }}
          </p>
        </div>
        <div class="modal-body">
          <div class="plan-options">
            <label
              v-for="plan in planOptions"
              :key="plan.id"
              class="plan-option"
              :class="{ 'plan-option--selected': selectedPriceId === plan.id }"
            >
              <input
                v-model="selectedPriceId"
                type="radio"
                name="change-plan-price"
                :value="plan.id"
              />
              <div class="plan-option__body">
                <div class="plan-option__row">
                  <span class="plan-option__name">
                    {{ plan.name }}
                    <span
                      v-if="plan.isCurrent"
                      class="plan-badge plan-badge--current"
                    >
                      {{ t("web.subscription.badge.current") }}
                    </span>
                    <span
                      v-else-if="plan.savePercent"
                      class="plan-badge plan-badge--save"
                    >
                      {{
                        t("web.subscription.badge.save_percent", {
                          percent: plan.savePercent,
                        })
                      }}
                    </span>
                  </span>
                  <span class="plan-option__price">{{ plan.price }}</span>
                </div>
                <div v-if="plan.description" class="plan-option__description">
                  {{ plan.description }}
                </div>
              </div>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="portal-btn portal-btn--ghost"
            type="button"
            @click="hideChangePlanModal"
          >
            {{ t("web.button.cancel") }}
          </button>
          <button
            class="portal-btn portal-btn--primary"
            type="button"
            :disabled="!canConfirmPlan"
            @click="confirmChangePlan"
          >
            {{ t("web.subscription.action.confirm_change") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Update-payment-method Modal -->
    <div
      v-if="showUpdateCardModal"
      class="modal-overlay"
      :class="{ show: updateCardModalVisible }"
      @click.self="hideUpdateCardModal"
    >
      <div class="modal-content modal-content--wide">
        <div class="modal-header">
          <h3 class="modal-title">
            {{ t("web.subscription.action.update_payment_method") }}
          </h3>
          <p class="modal-subtitle">
            {{ t("web.subscription.message.card_secured_by_stripe") }}
          </p>
        </div>
        <UpdatePaymentMethodForm
          v-if="subscriber"
          :newsletter="newsletter"
          :subscriber-id="subscriber.id"
          :current-card="cardOnFile"
          @cancel="hideUpdateCardModal"
        />
      </div>
    </div>
  </Teleport>

  <!-- Subscriber Comments Section -->
  <Teleport to="#subscriber-comments">
    <template v-if="comments.length > 0">
      <hr class="section-divider" />
      <div>
        <h2 class="section-title">
          {{ t("web.subscription.label.your_comments") }}
        </h2>
        <ol class="archive__comments-list">
          <li
            v-for="comment in comments"
            :key="comment.id"
            class="archive__comment-item"
          >
            <div class="comment">
              <div class="comment__content">
                {{ truncate(comment.text, 200) }}
              </div>
              <div class="comment__footer">
                <div class="comment__actions">
                  <a :href="comment.email?.absolute_url">
                    {{ comment.email?.subject }}
                  </a>
                  <span class="comment__timestamp">
                    {{ formatDate(comment.creation_date) }}
                  </span>
                  <a
                    href="#"
                    class="comment-delete-button"
                    @click.prevent="deleteComment(comment.id)"
                  >
                    {{
                      deletingCommentId === comment.id
                        ? t("web.subscription.action.deleting")
                        : t("web.button.delete")
                    }}
                  </a>
                </div>
              </div>
            </div>
          </li>
        </ol>
      </div>
    </template>
  </Teleport>
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  ref,
} from "vue";

import type { components as InternalOpenAPI } from "@/autogen/internal-openapi";
import type { components, paths } from "@/autogen/openapi";
import { createClient } from "@/lib/openapi";
import { extractEmailIdFromUrl } from "@/lib/subscribers/extractEmailIdFromUrl";
import type { Newsletter, NewsletterPrice as Price } from "@/types/newsletter";

import { createTranslator } from "./lib/utils";

// The same inline payment form the subscribe widget uses; mounted here so an
// existing free subscriber can upgrade in-place (POSTs to /create-subscriber,
// which upgrades them via CollisionBehavior.ADD) instead of bouncing to hosted
// Stripe Checkout.
const PaymentElement = defineAsyncComponent(
  () => import("./SubscribeForm/components/PaymentElement.vue")
);

// Card-collection form for an existing premium subscriber updating their card,
// without bouncing to Stripe's hosted billing portal.
const UpdatePaymentMethodForm = defineAsyncComponent(
  () => import("./UpdatePaymentMethodForm.vue")
);

const api = createClient<paths>({ base: "/v1" });

// The #subscriber blob is BasicSubscriberSchema
// (emails/views/subscriber_facing/schemas.py), exposed in the internal autogen.
type SubscriberData = InternalOpenAPI["schemas"]["BasicSubscriber"];
type CommentData = components["schemas"]["Comment"];

const props = defineProps<{
  newsletter: Newsletter;
}>();

const t = createTranslator(() => props.newsletter.locale);

// Reactive state
const subscriber = ref<SubscriberData | null>(null);
const showUnsubscribeModal = ref(false);
const unsubscribeModalVisible = ref(false);
const showConfirmationModal = ref(false);
const confirmationModalVisible = ref(false);
const showUpgradeModal = ref(false);
const upgradeModalVisible = ref(false);
const showChangePlanModal = ref(false);
const changePlanModalVisible = ref(false);
const showUpdateCardModal = ref(false);
const updateCardModalVisible = ref(false);
const selectedReason = ref("");
const selectedPriceId = ref("");
const currentPriceId = ref<string | null>(null);
const cardOnFile = ref<CardOnFile | null>(null);
const isProcessing = ref(false);

interface CardOnFile {
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

interface PlanOption {
  id: string;
  name: string;
  price: string;
  description: string;
  isCurrent: boolean;
  savePercent: number | null;
}

const recurringPrices = computed(() =>
  (props.newsletter.prices ?? []).filter(
    (price) => price.cadence === "month" || price.cadence === "year"
  )
);

function formatAmount(price: Price): string {
  const amount = new Intl.NumberFormat(props.newsletter.locale || "en", {
    style: "currency",
    currency: (price.currency || "usd").toUpperCase(),
  }).format((price.amount ?? 0) / 100);
  return `${amount} / ${price.cadence === "year" ? "yr" : "mo"}`;
}

const planOptions = computed<PlanOption[]>(() => {
  const prices = recurringPrices.value;
  const monthly = prices.find((price) => price.cadence === "month");
  return prices.map((price) => {
    let savePercent: number | null = null;
    if (price.cadence === "year" && monthly?.amount && price.amount) {
      const yearlyIfPaidMonthly = monthly.amount * 12;
      if (price.amount < yearlyIfPaidMonthly) {
        savePercent = Math.round(
          (1 - price.amount / yearlyIfPaidMonthly) * 100
        );
      }
    }
    return {
      id: price.id,
      name:
        price.cadence === "year"
          ? t("web.cadence.annual")
          : t("web.cadence.monthly"),
      price: formatAmount(price),
      description: price.description ?? "",
      isCurrent: currentPriceId.value === price.id,
      savePercent,
    };
  });
});

const canConfirmPlan = computed(
  () =>
    !!selectedPriceId.value &&
    !isProcessing.value &&
    selectedPriceId.value !== currentPriceId.value
);
const comments = ref<CommentData[]>([]);
const deletingCommentId = ref<string | null>(null);

function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + "…";
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(
    props.newsletter.locale || "en",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
}

// Modal helpers
function openUnsubscribeModal() {
  showUnsubscribeModal.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      unsubscribeModalVisible.value = true;
    });
  });
}

function hideUnsubscribeModal() {
  unsubscribeModalVisible.value = false;
  setTimeout(() => {
    showUnsubscribeModal.value = false;
  }, 200);
}

function openConfirmationModal() {
  showConfirmationModal.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      confirmationModalVisible.value = true;
    });
  });
}

function openUpgradeModal() {
  showUpgradeModal.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      upgradeModalVisible.value = true;
    });
  });
}

function hideUpgradeModal() {
  upgradeModalVisible.value = false;
  setTimeout(() => {
    showUpgradeModal.value = false;
  }, 200);
}

function openChangePlanModal() {
  showChangePlanModal.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      changePlanModalVisible.value = true;
    });
  });
}

function hideChangePlanModal() {
  changePlanModalVisible.value = false;
  setTimeout(() => {
    showChangePlanModal.value = false;
  }, 200);
}

function openUpdateCardModal() {
  showUpdateCardModal.value = true;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      updateCardModalVisible.value = true;
    });
  });
}

function hideUpdateCardModal() {
  updateCardModalVisible.value = false;
  setTimeout(() => {
    showUpdateCardModal.value = false;
  }, 200);
}

// Pause/resume/change-plan all POST to the subscriber-facing subscription
// endpoints (gated by the subscriber's UUID, like the rest of the portal) and
// reload so the server re-renders the updated state.
async function postSubscriptionAction(
  path: string,
  body: Record<string, unknown>,
  button?: HTMLElement | null
) {
  if (!subscriber.value || isProcessing.value) return;
  isProcessing.value = true;
  if (button) {
    button.setAttribute("disabled", "true");
    button.innerText = t("web.subscription.action.processing");
  }
  try {
    const response = await fetch(
      `/api/emails/subscriptions/${subscriber.value.id}/${path}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );
    if (!response.ok) throw new Error("Request failed");
    window.location.reload();
  } catch (error) {
    console.error("Error:", error);
    alert(t("web.message.error.generic"));
    isProcessing.value = false;
    if (button) button.removeAttribute("disabled");
  }
}

function confirmChangePlan() {
  if (!selectedPriceId.value) return;
  postSubscriptionAction("change-plan", { price_id: selectedPriceId.value });
}

// API calls
async function processSubscriptionChange(
  subscriberType: components["schemas"]["SubscriberType"],
  button?: HTMLElement
) {
  if (!subscriber.value || isProcessing.value) return;

  isProcessing.value = true;
  if (button) {
    button.setAttribute("disabled", "true");
    button.innerText = t("web.subscription.action.processing");
  }

  try {
    await api.patch("/subscribers/{id_or_email}", {
      params: { path: { id_or_email: subscriber.value.id } },
      body: { type: subscriberType },
    });
    window.location.reload();
  } catch (error) {
    console.error("Error:", error);
    alert(t("web.message.error.generic"));
    isProcessing.value = false;
    if (button) {
      button.removeAttribute("disabled");
      button.innerText = t("web.button.resubscribe");
    }
  }
}

// Unsubscribe runs through the unauthenticated /unsubscribe endpoint rather than
// the session-gated PATCH: a subscriber arriving cold via a footer link has no
// verified session (decision 0172 keeps unsubscribe one-click). Derive the
// endpoint from the management URL, which shares the same host and optional
// /{username} prefix.
async function submitUnsubscribe(button?: HTMLElement) {
  if (!subscriber.value || isProcessing.value) return;

  isProcessing.value = true;
  if (button) {
    button.setAttribute("disabled", "true");
    button.innerText = t("web.subscription.action.processing");
  }

  const unsubscribeUrl = window.location.pathname.replace(
    /management\/subscribers\/[^/]+\/?$/,
    `unsubscribe/${subscriber.value.id}`
  );

  const body = new URLSearchParams();
  // Opt out of the canary bot-guard: this is an explicit, human-confirmed
  // unsubscribe, not link-prefetch or a List-Unsubscribe-Post.
  body.set("from_management_page", "1");
  if (selectedReason.value) {
    body.set("unsubscription_reason", selectedReason.value);
  }
  const email = window.location.search.includes("email=")
    ? window.location.search.split("email=")[1].split("&")[0]
    : null;
  const extractedEmailId = extractEmailIdFromUrl(email);
  if (extractedEmailId) {
    body.set("email", extractedEmailId);
  }

  try {
    const response = await fetch(unsubscribeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!response.ok) throw new Error("Unsubscribe failed");
    window.location.reload();
  } catch (error) {
    console.error("Error:", error);
    alert(t("web.message.error.generic"));
    isProcessing.value = false;
    if (button) {
      button.removeAttribute("disabled");
      button.innerText = t("web.button.unsubscribe");
    }
  }
}

async function saveChanges(
  emailInput: HTMLInputElement | null,
  referralCodeInput: HTMLInputElement | null,
  referralLink: HTMLElement | null,
  saveChangesButton: HTMLElement
) {
  if (!subscriber.value) return;

  const email = emailInput?.value.trim() ?? null;
  const referralCode = referralCodeInput?.value.trim().toLowerCase() ?? null;

  if (email && !email.includes("@")) {
    alert(t("web.subscription.message.invalid_email_address"));
    return;
  }

  if (
    referralCode &&
    (referralCode.length !== 6 || !/^[a-z]{6}$/.test(referralCode))
  ) {
    alert(t("web.subscription.message.referral_code_format"));
    return;
  }

  const body: paths["/subscribers/{id_or_email}"]["patch"]["requestBody"]["content"]["application/json"] =
    {};
  if (email) body.email_address = email;

  saveChangesButton.setAttribute("disabled", "true");
  saveChangesButton.innerText = t("web.subscription.action.saving");

  try {
    const { error } = await api.patch("/subscribers/{id_or_email}", {
      params: { path: { id_or_email: subscriber.value.id } },
      body,
    });

    if (error) {
      throw new Error("An error occurred. Please try again.");
    }

    if (referralCode && referralLink) {
      referralLink.innerText =
        props.newsletter.absolute_url + "?utm_referral=" + referralCode;
    }

    window.location.reload();
  } catch (error) {
    console.error("Error:", error);
    alert(t("web.message.error.generic"));
    window.location.reload();
  }
}

async function updateMetadata(updateButton: HTMLElement) {
  if (!subscriber.value) return;

  const metadataFields = document.querySelectorAll(".metadata-field");
  const metadata: Record<string, string | boolean> = {};

  metadataFields.forEach((field) => {
    const el = field as HTMLInputElement | HTMLSelectElement;
    const key = el.dataset.fieldKey;
    const type = el.dataset.fieldType;
    if (!key) return;
    if (type === "checkbox") {
      metadata[key] = (el as HTMLInputElement).checked;
    } else {
      metadata[key] = el.value;
    }
  });

  const tagFields = document.querySelectorAll(".tag-field");
  const tags: string[] = [];
  tagFields.forEach((field) => {
    const el = field as HTMLInputElement;
    if (el.checked) tags.push(el.dataset.tagId!);
  });

  updateButton.setAttribute("disabled", "true");
  updateButton.innerText = t("web.subscription.action.updating");

  const body: paths["/subscribers/{id_or_email}"]["patch"]["requestBody"]["content"]["application/json"] =
    {};
  if (Object.keys(metadata).length > 0) body.metadata = metadata;
  if (tagFields.length > 0) body.tags = tags;

  try {
    const { error } = await api.patch("/subscribers/{id_or_email}", {
      params: { path: { id_or_email: subscriber.value.id } },
      body,
    });

    if (!error) {
      window.location.reload();
    } else {
      alert(t("web.message.error.generic"));
      updateButton.removeAttribute("disabled");
      updateButton.innerText = t("web.button.update");
    }
  } catch (error) {
    console.error("Error:", error);
    alert(t("web.message.error.generic"));
    updateButton.removeAttribute("disabled");
    updateButton.innerText = t("web.button.update");
  }
}

async function fetchComments() {
  if (!props.newsletter.enabled_features?.includes("comments")) return;

  try {
    const response = await fetch(
      "/v1/comments?expand=email&ordering=-creation_date"
    );
    if (!response.ok) return;
    const data = await response.json();
    comments.value = data.results ?? [];
  } catch {
    // Silently fail — comments are supplementary
  }
}

async function deleteComment(commentId: string) {
  if (!confirm(t("web.comment.message.delete_confirm"))) return;

  deletingCommentId.value = commentId;
  try {
    const response = await fetch(`/v1/comments/${commentId}`, {
      method: "DELETE",
    });
    if (response.ok || response.status === 204) {
      comments.value = comments.value.filter((c) => c.id !== commentId);
    } else {
      alert(t("web.message.error.generic"));
    }
  } catch {
    alert(t("web.message.error.generic"));
  } finally {
    deletingCommentId.value = null;
  }
}

function handleRefresh() {
  window.location.reload();
}

function confirmUnsubscribe() {
  const subscriptionButton = document.getElementById("subscription-button");
  hideUnsubscribeModal();
  submitUnsubscribe(subscriptionButton ?? undefined);
}

// Keyboard handler
function handleKeydown(e: KeyboardEvent) {
  if (e.key === "Escape" && unsubscribeModalVisible.value) {
    hideUnsubscribeModal();
  }
  if (e.key === "Escape" && upgradeModalVisible.value) {
    hideUpgradeModal();
  }
  if (e.key === "Escape" && changePlanModalVisible.value) {
    hideChangePlanModal();
  }
  if (e.key === "Escape" && updateCardModalVisible.value) {
    hideUpdateCardModal();
  }
}

onMounted(async () => {
  const subscriberElement = document.getElementById("subscriber");
  if (!subscriberElement) return;

  await import("@/subscriber_facing/styles/manage-subscription.css");

  subscriber.value = JSON.parse(subscriberElement.textContent || "{}");

  // Current card + active plan are injected as json_script by the portal view
  // (manage_subscription.py), so the modals can render them without a Stripe call.
  const cardElement = document.getElementById("card_on_file");
  if (cardElement) {
    cardOnFile.value = JSON.parse(cardElement.textContent || "null");
  }
  const priceElement = document.getElementById("current_price_id");
  if (priceElement) {
    currentPriceId.value = JSON.parse(priceElement.textContent || "null");
    // Pre-select the active plan, like the design.
    if (currentPriceId.value) selectedPriceId.value = currentPriceId.value;
  }

  // DOM references for server-rendered form elements
  const subscriptionButton = document.getElementById("subscription-button");
  const updateButton = document.getElementById("update-button");
  const saveChangesButton = document.getElementById("save-changes-button");
  const emailInput = document.getElementById(
    "email-input"
  ) as HTMLInputElement | null;
  const referralCodeInput = document.getElementById(
    "referral-code-input"
  ) as HTMLInputElement | null;
  const referralLink = document.getElementById("referral-link");

  // Auto-open unsubscribe modal from URL hash
  if (window.location.hash === "#unsubscribe") {
    if (subscriber.value?.type !== "unsubscribed") {
      openUnsubscribeModal();
    } else {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }

  // Show confirmation modal for unactivated subscribers
  if (subscriber.value?.type === "unactivated") {
    openConfirmationModal();
  }

  // Attach event listeners to server-rendered buttons
  subscriptionButton?.addEventListener("click", function () {
    const action = (this as HTMLElement).dataset.action;
    if (action === "unsubscribe") {
      openUnsubscribeModal();
      return;
    }
    processSubscriptionChange("regular", subscriptionButton);
  });

  saveChangesButton?.addEventListener("click", () => {
    saveChanges(emailInput, referralCodeInput, referralLink, saveChangesButton);
  });

  updateButton?.addEventListener("click", () => {
    updateMetadata(updateButton);
  });

  const upgradeButton = document.getElementById("upgrade-button");
  upgradeButton?.addEventListener("click", openUpgradeModal);

  const pauseButton = document.getElementById("pause-subscription-button");
  pauseButton?.addEventListener("click", () =>
    postSubscriptionAction("pause", {}, pauseButton)
  );

  const resumeButton = document.getElementById("resume-subscription-button");
  resumeButton?.addEventListener("click", () =>
    postSubscriptionAction("resume", {}, resumeButton)
  );

  // Cancel is a soft cancel-at-period-end (the subscriber keeps access until
  // their paid-through date and can reactivate), but it's still the most
  // consequential action on the card, so gate it behind a confirmation.
  const cancelButton = document.getElementById("cancel-subscription-button");
  cancelButton?.addEventListener("click", () => {
    if (!confirm(t("web.subscription.message.cancel_confirm"))) return;
    postSubscriptionAction("cancel", {}, cancelButton);
  });

  const reactivateButton = document.getElementById(
    "reactivate-subscription-button"
  );
  reactivateButton?.addEventListener("click", () =>
    postSubscriptionAction("reactivate", {}, reactivateButton)
  );

  document
    .getElementById("change-plan-button")
    ?.addEventListener("click", openChangePlanModal);

  document
    .getElementById("update-payment-method-button")
    ?.addEventListener("click", openUpdateCardModal);

  // Auto-open from an #upgrade deep link (mirrors the #unsubscribe handling
  // above). The button only renders for non-premium subscribers on a paid
  // newsletter, so its presence is the gate.
  if (window.location.hash === "#upgrade" && upgradeButton) {
    openUpgradeModal();
  }

  document.addEventListener("keydown", handleKeydown);

  // Fetch comments async
  fetchComments();
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>
